import React, { useContext, useMemo } from 'react';
import Highcharts from 'highcharts/esm/highcharts.js';
import ReactDOMServer from 'react-dom/server';
import { FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { RAGLabel } from '@components-clean/core/charts/shared/utils';
import { PopOutPage } from '@components-clean/core/page-toolbar/page-toolbar';
import PanelFrame from '@components-clean/core/panel-frame';
import { SiteHubPanelScoreData } from '@interfaces/central-monitoring';
import { LineSeriesDataProperties } from '@interfaces-clean/panels.interface';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { Permissions } from 'contexts/user-context';
import { PanelSeriesType, PanelType } from 'types/graphql-global-types';
import { UserContext } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowMessage } from '@utils/notification';
import { AllPaths } from '@utils/router/all-paths';

interface IProps {
    panel: SiteHubPanelScoreData;
    showLegend?: boolean;
    hidePanelSetup?: boolean;
    siteId?: number;
}

const SiteHubChart = ({ panel, showLegend = false, hidePanelSetup = true, siteId }: IProps) => {
    const { t } = useTranslation();
    const { current } = useContext(StudySiteHubContext);
    const { reference, plotTitle, datasets, dataviewRef } = panel;
    const { user } = useContext(UserContext);
    const sparkLineData = useMemo(() => {
        return [
            datasets.map((d) => {
                return { x: d.date, y: d.plot, color: d.colour, score: d.score };
            }),
        ];
    }, [panel]);

    const dataViewRoute = AllPaths.path('cm2-data-views', {
        dataView: dataviewRef,
    });

    const popoutDataview = (ragColor: number | undefined, datasetCutDate: string) => {
        if (
            !user?.can?.({
                module: Constants.Modules.CM2,
                page: Constants.PageElements.DataViewsCM2,
                permission: Permissions.Enabled,
                element: Constants.PageElements.DataViewsCM2,
            })
        )
            return;

        if (ragColor !== undefined) {
            PopOutPage(user?.organisationKey, `${dataViewRoute}?datasetcutdate=${datasetCutDate}&siteid=${siteId}`);
        }
    };

    const getCategories = () => {
        const comparisonDatasetDate = current?.datasetFilters?.comparisonDataset?.dataCutDate;
        return datasets.map((dataset) => {
            const dataSetDate = stringDateWithoutTimeFromUtc(dataset.date, 'DD-MMM-YYYY')!;
            if (dataSetDate === comparisonDatasetDate) return `<b>${dataSetDate}</b>`;
            return dataSetDate;
        });
    };

    return (
        <PanelFrame
            title={plotTitle}
            data-testid={`panel-${reference}`}
            panelType={PanelType.GRAPH}
            seriesConfig={{
                type: PanelSeriesType.SITE,
            }}
            graphType="SPARKLINE"
            showLegend={showLegend}
            hidePanelSetup={hidePanelSetup}
            panelData={{
                sparkLineData,
                categories: getCategories(),
            }}
            customOptions={{
                plotOptions: {
                    series: {
                        color: styles['primary-line-color'],
                        states: {
                            hover: {
                                lineWidthPlus: 0,
                            },
                        },
                        point: {
                            events: {
                                click(eve) {
                                    if (dataviewRef)
                                        popoutDataview(eve.point.colorIndex as number, eve.point.category as string);
                                    else if (eve.point.colorIndex !== undefined)
                                        ShowMessage({
                                            text: t('cm:componentsClean.panels.noDataviewConfigured'),
                                            type: 'none',
                                        });
                                },
                            },
                        },
                    },
                },
                tooltip: {
                    formatter() {
                        const point = this as Highcharts.Point;
                        const customProps = point.options as LineSeriesDataProperties;

                        return ReactDOMServer.renderToStaticMarkup(
                            <div>
                                <br />
                                {t('cm:componentsClean:charts:score')}: {FormatNumberForChart(customProps.score)}
                                <br />
                                {t('cm:componentsClean:charts:rag')}: {RAGLabel(customProps.colorIndex as number)}
                                <br />
                                {t('cm:siteHub:sparkLine:tooltip:siteRate')}: {point.y}
                            </div>,
                        );
                    },
                },
            }}
        />
    );
};

export default SiteHubChart;
